import { useEffect, useState } from "react";
import { Card, Button, Modal, Form, Row, Col, Spinner } from "react-bootstrap";
import { adminGet, adminPost, adminDelete, adminPut } from "../../../Services";
import { toast } from "react-toastify";

const AdminGallery = () => {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupImages, setGroupImages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newImages, setNewImages] = useState([]);
  const [newGroupName, setNewGroupName] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDeleteImageModal, setShowDeleteImageModal] = useState(false);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState(null);

  // Fetch groups with preview images
  const fetchGroups = async () => {
    setLoading(true);
    try {
      const res = await adminGet("/api/gallery/groups");
      setGroups(res.data);
    } catch (err) {
      toast.error("Failed to fetch groups");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  // Fetch images of a group
  const fetchGroupImages = async (groupName) => {
    try {
      const res = await adminGet(`/api/gallery/group/${groupName}`);
      setGroupImages(res.data);
      setSelectedGroup(groupName);
    } catch (err) {
      toast.error("Failed to fetch images");
    }
  };

  // Upload images to selected group
  const handleUpload = async () => {
    if (!newImages.length || !selectedGroup) return;
    const formData = new FormData();
    newImages.forEach((file) => formData.append("images", file));
    formData.append("groupName", selectedGroup);

    try {
      await adminPost("/api/gallery", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Images uploaded!");
      setNewImages([]);
      fetchGroupImages(selectedGroup);
    } catch (err) {
      toast.error("Upload failed");
    }
  };

  // Update group name
  const handleUpdateGroupName = async () => {
    if (!newGroupName || !selectedGroup) return;
    try {
      await adminPut("/api/gallery/update-group", {
        oldName: selectedGroup,
        newName: newGroupName.trim(),
      });
      toast.success("Group name updated");
      setSelectedGroup(newGroupName);
      setNewGroupName("");
      fetchGroups();
    } catch (err) {
      toast.error("Failed to update group name");
    }
  };

  // Create new group
  const handleCreateGroup = async () => {
    if (!newGroupName || !newImages.length) {
      toast.error("Group name and at least one image are required");
      return;
    }

    const formData = new FormData();
    newImages.forEach((file) => formData.append("images", file));
    formData.append("groupName", newGroupName.trim());

    try {
      await adminPost("/api/gallery", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("New group created!");
      setNewGroupName("");
      setNewImages([]);
      setShowModal(false);
      fetchGroups();
    } catch (err) {
      toast.error("Failed to create group");
    }
  };

  const confirmDeleteImage = (id) => {
    setSelectedImageId(id);
    setShowDeleteImageModal(true);
  };

  const handleDeleteImage = async () => {
    try {
      await adminDelete(`/api/gallery/${selectedImageId}`);
      toast.success("Image deleted");
      fetchGroupImages(selectedGroup);
    } catch (err) {
      toast.error("Failed to delete image");
    } finally {
      setShowDeleteImageModal(false);
      setSelectedImageId(null);
    }
  };

  const confirmDeleteGroup = () => {
    setShowDeleteGroupModal(true);
  };

  const handleDeleteGroup = async () => {
    try {
      await adminDelete(`/api/gallery/group/${selectedGroup}`);
      toast.success("Group deleted");
      setSelectedGroup(null);
      setGroupImages([]);
      fetchGroups();
    } catch (err) {
      toast.error("Failed to delete group");
    } finally {
      setShowDeleteGroupModal(false);
    }
  };

  return (
    <div className="">
      <div className="d-flex align-items-center justify-content-between">
        <h3 className="fw-medium" style={{ color: "#1a5276" }}>
          Gallery Management
        </h3>

        <Button className="mb-3" onClick={() => setShowModal(true)}>
          + Create New Group
        </Button>
      </div>

      {/* Display groups with preview */}
      <Row className="my-3">
        {loading ? (
          <Spinner animation="border" />
        ) : (
          groups.map((group) => (
            <Col md={3} key={group.groupName} className="mb-3">
              <Card
                onClick={() => fetchGroupImages(group.groupName)}
                style={{ cursor: "pointer" }}
              >
                <Card.Img
                  variant="top"
                  src={`${group.previewImage}`}
                  style={{ height: "200px", objectFit: "cover" }}
                />
                <Card.Body>
                  <Card.Title>{group.groupName}</Card.Title>
                </Card.Body>
              </Card>
            </Col>
          ))
        )}
      </Row>

      {/* Selected group details */}
      {selectedGroup && (
        <>
          <h4 className="mt-4">Group: {selectedGroup}</h4>

          {/* Update group name */}
          <div className="mb-3 d-flex align-items-center justify-content-between">
            <Form className="d-flex align-items-center gap-2">
              <Form.Control
                type="text"
                style={{ width: "40vw" }}
                placeholder={selectedGroup}
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                className=""
              />
              <Button variant="primary" onClick={handleUpdateGroupName}>
                Rename Group
              </Button>
            </Form>
            <Button
              variant="danger"
              className="mb-3"
              onClick={confirmDeleteGroup}
            >
              Delete Entire Group
            </Button>
          </div>

          {/* Upload images */}
          <Form className="mb-3">
            <Form.Control
              type="file"
              multiple
              onChange={(e) => setNewImages([...e.target.files])}
            />
            <Button className="mt-2" onClick={handleUpload}>
              Upload to Group
            </Button>
          </Form>

          {/* Display images */}
          <Row>
            {groupImages.map((img) => (
              <Col md={3} key={img.id} className="mb-3">
                <Card>
                  <Card.Img
                    variant="top"
                    src={`${img.imageUrl}`}
                    style={{
                      height: "200px",
                      objectFit: "cover",
                      cursor: "pointer",
                    }}
                    onClick={() => window.open(`${img.imageUrl}`, "_blank")}
                  />
                  <Card.Body>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => confirmDeleteImage(img.id)}
                    >
                      Delete
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}

      {/* Modal for creating new group */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Group Name</Form.Label>
              <Form.Control
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Enter group name"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Upload Images</Form.Label>
              <Form.Control
                type="file"
                multiple
                onChange={(e) => setNewImages([...e.target.files])}
              />
            </Form.Group>
            <Button className="mt-2" onClick={handleCreateGroup}>
              Create Group
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Image delete confirmation modal */}
      <Modal
        show={showDeleteImageModal}
        onHide={() => setShowDeleteImageModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this image?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteImageModal(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteImage}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Group delete confirmation modal */}
      <Modal
        show={showDeleteGroupModal}
        onHide={() => setShowDeleteGroupModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the entire group "{selectedGroup}" and
          all its images?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteGroupModal(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteGroup}>
            Delete Group
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminGallery;
